/**
 * Network Service - Modular Architecture
 * 
 * Unified export system providing complete NetworkService functionality
 * through composition of specialized modules while maintaining 100%
 * backward compatibility with the original API.
 */

import {
  BaseNetworkService,
  NetworkConfig,
  NetworkEvent,
  NetworkEventListener,
  NetworkQuality,
  NetworkStats,
  DEFAULT_NETWORK_CONFIG
} from './BaseNetworkService';
import { NetworkMonitor } from './NetworkMonitor';
import { QualityDetector } from './QualityDetector';
import { EventManager } from './EventManager';
import { CapacitorIntegration } from './CapacitorIntegration';

/**
 * Complete Network Service
 * 
 * Combines all modular components to provide the full NetworkService
 * functionality with enhanced maintainability and extensibility.
 */
export class NetworkService extends BaseNetworkService {
  private networkMonitor: NetworkMonitor;
  private qualityDetector: QualityDetector;
  private eventManager: EventManager;
  private capacitorIntegration: CapacitorIntegration;
  
  // Timers
  private syncTimer: NodeJS.Timeout | null = null;

  constructor(config: Partial<NetworkConfig> = {}) {
    super(config);
    
    // Initialize modular components
    this.networkMonitor = new NetworkMonitor(this.config.enableDebugLogging);
    this.qualityDetector = new QualityDetector(
      this.config.qualityCheckInterval,
      this.config.qualityCheckTimeout,
      this.config.qualityCheckUrl,
      this.config.enableDebugLogging
    );
    this.eventManager = new EventManager(
      { enabled: true, interval: 1000, maxEvents: 10 },
      this.config.enableDebugLogging
    );
    this.capacitorIntegration = new CapacitorIntegration(this.config.enableDebugLogging);

    // Set up cross-component event handling
    this.setupEventHandling();
  }

  /**
   * Initialize the network service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      this.debugLog('🌐 Initializing Network Service...');

      // Get initial network status using platform integration
      this.currentStatus = await this.capacitorIntegration.getNetworkStatus();
      
      if (this.currentStatus.connected) {
        this.handleNetworkConnected();
      } else {
        this.handleNetworkDisconnected();
      }

      // Start network monitoring
      await this.networkMonitor.startMonitoring();

      // Start quality monitoring if enabled and connected
      if (this.config.enableQualityMonitoring && this.currentStatus.connected) {
        this.qualityDetector.startMonitoring(this.currentStatus);
      }

      this.isInitialized = true;
      
      this.debugLog('✅ Network Service initialized successfully');
      this.debugLog('🌐 Initial network status:', this.currentStatus);
    } catch (error) {
      console.error('❌ Failed to initialize Network Service:', error);
      throw error;
    }
  }

  /**
   * Add event listener
   */
  addEventListener(listener: NetworkEventListener): void {
    this.eventManager.addEventListener(listener);
  }

  /**
   * Remove event listener
   */
  removeEventListener(listener: NetworkEventListener): void {
    this.eventManager.removeEventListener(listener);
  }

  /**
   * Get current network quality
   */
  getCurrentQuality(): NetworkQuality | null {
    return this.qualityDetector.getCurrentQuality();
  }

  /**
   * Get quality history
   */
  getQualityHistory(): NetworkQuality[] {
    return this.qualityDetector.getQualityHistory();
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<NetworkConfig>): void {
    super.updateConfig(updates);
    
    // Update quality detector configuration
    this.qualityDetector.updateConfig({
      qualityCheckInterval: this.config.qualityCheckInterval,
      qualityCheckTimeout: this.config.qualityCheckTimeout,
      qualityCheckUrl: this.config.qualityCheckUrl
    });

    // Restart quality monitoring if interval changed and we're connected
    if (updates.qualityCheckInterval && this.config.enableQualityMonitoring && this.currentStatus?.connected) {
      this.qualityDetector.startMonitoring(this.currentStatus);
    }
  }

  /**
   * Cleanup resources
   */
  cleanup(): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }

    // Cleanup all modules
    this.networkMonitor.cleanup();
    this.qualityDetector.cleanup();
    this.eventManager.cleanup();
    this.capacitorIntegration.cleanup();
    
    this.isInitialized = false;
    this.debugLog('🧹 Network Service cleaned up');
  }

  /**
   * Set up event handling between components
   */
  private setupEventHandling(): void {
    // Network monitor events
    this.networkMonitor.addEventListener((event) => {
      this.handleNetworkEvent(event);
      this.eventManager.emitEvent(event);
    });

    // Quality detector events
    this.qualityDetector.addEventListener((event) => {
      this.handleQualityEvent(event);
      this.eventManager.emitEvent(event);
    });
  }

  /**
   * Handle network events from monitor
   */
  private handleNetworkEvent(event: NetworkEvent): void {
    if (event.type === 'network_connected') {
      this.updateConnectionStats(true, event.data.timestamp);
      this.currentStatus = { connected: true, connectionType: event.data.connectionType };
      
      // Start quality monitoring
      if (this.config.enableQualityMonitoring) {
        this.qualityDetector.startMonitoring(this.currentStatus);
      }

      // Trigger sync if enabled
      if (this.config.syncOnNetworkRestore) {
        this.scheduleSync('network_restored', this.config.networkRestoreDelay);
      }
    } else if (event.type === 'network_disconnected') {
      this.updateConnectionStats(false, event.data.timestamp);
      this.currentStatus = { connected: false, connectionType: 'none' };
      
      // Stop quality monitoring
      this.qualityDetector.stopMonitoring();
    } else if (event.type === 'connection_type_changed') {
      if (this.currentStatus) {
        this.currentStatus.connectionType = event.data.to;
      }

      // Trigger sync if enabled and connection type is preferred
      if (this.config.syncOnConnectionTypeChange && 
          this.config.preferredConnectionTypes.includes(event.data.to)) {
        this.scheduleSync('connection_type_changed', this.config.connectionChangeDelay);
      }
    }
  }

  /**
   * Handle quality events from detector
   */
  private handleQualityEvent(event: NetworkEvent): void {
    if (event.type === 'network_quality_changed') {
      // Trigger sync if quality improved
      if (this.config.syncOnQualityImprovement) {
        const currentQuality = this.qualityDetector.getCurrentQuality();
        if (currentQuality && this.qualityDetector.isQualityBetter(
          event.data.quality, 
          currentQuality.quality
        )) {
          this.scheduleSync('quality_improved', 0);
        }
      }
    }
  }

  /**
   * Handle initial network connected state
   */
  private handleNetworkConnected(): void {
    const timestamp = new Date().toISOString();
    this.updateConnectionStats(true, timestamp);

    // Start quality monitoring
    if (this.config.enableQualityMonitoring) {
      this.qualityDetector.startMonitoring(this.currentStatus);
    }
  }

  /**
   * Handle initial network disconnected state
   */
  private handleNetworkDisconnected(): void {
    const timestamp = new Date().toISOString();
    this.updateConnectionStats(false, timestamp);

    // Stop quality monitoring
    this.qualityDetector.stopMonitoring();
  }

  /**
   * Schedule sync operation
   */
  private scheduleSync(trigger: string, delay: number): void {
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
    }

    this.syncTimer = setTimeout(async () => {
      await this.triggerSync(trigger);
    }, delay);
  }

  /**
   * Trigger sync operation
   */
  private async triggerSync(trigger: string): Promise<void> {
    try {
      this.debugLog(`🔄 Triggering sync due to: ${trigger}`);

      this.eventManager.emitSyncTriggered(trigger, this.currentStatus!);

      // Import and trigger the main sync service
      const { syncService } = await import('@/services/offline');
      await syncService.performSync();
    } catch (error) {
      console.error(`❌ Failed to trigger sync (${trigger}):`, error);
    }
  }
}

// Re-export all types for backward compatibility
export type {
  NetworkConfig,
  NetworkEvent,
  NetworkEventListener,
  NetworkQuality,
  NetworkStats
};

// Export modular components for advanced usage
export {
  NetworkMonitor,
  QualityDetector,
  EventManager,
  CapacitorIntegration,
  BaseNetworkService,
  DEFAULT_NETWORK_CONFIG
};

// Export singleton instance for backward compatibility
export const networkService = new NetworkService();
